/**
 * Le moteur qui juge une feuille de brouillon, derrière une seule interface.
 *
 *  - `reasonerEngine` : l'ancien moteur (`@jeraisonne/reasoner`, gelé), qui lit les BOUTS ;
 *  - `packsEngine` : le moteur v2 (`@jeraisonne/brouillon-engine`, lot V3), qui lit le
 *    TEXTE de la ligne — celui que l'app enregistre (`line_text`) et que le serveur rejoue.
 *    Ses verdicts sont traduits dans les genres de la feuille (`VerdictKind`) : la feuille,
 *    ses bulles et son économie d'HORA ne changent pas.
 *
 * Le module du moteur v2 est chargé à la demande (`loadPacksModule`) : une feuille de
 * l'ancien moteur ne le télécharge jamais.
 */
import { importWithRetry } from '@/lib/chunk-reload';
import {
  answerContains,
  claimDecisions,
  commit,
  createState,
  goalReached,
  hasLine,
  hint as reasonerHint,
  isAnswer,
  judge,
  parseText,
  sheetLineChips,
  statedValues,
  type Chip,
  type ReasonerState,
  type ScratchModel,
  type Trap,
  type Verdict,
  type VerdictKind,
} from '@jeraisonne/reasoner';
import type * as PacksApp from '@jeraisonne/brouillon-engine/app';

/** Le module du moteur v2 (façade `@jeraisonne/brouillon-engine/app`). */
export type PacksModule = typeof PacksApp;
type PacksSheet = ReturnType<PacksModule['openPacksSheet']>;
type PacksVerdict = ReturnType<PacksModule['judgePacksLine']>;

/** Version d'une feuille du moteur v2 (`scratch_models.engine_version`) : miroir de `PACKS_ENGINE_VERSION`. */
export const PACKS_ENGINE_VERSION = 100;

export function loadPacksModule(): Promise<PacksModule> {
  return importWithRetry(() => import('@jeraisonne/brouillon-engine/app'));
}

/** Le coup de pouce, dans la forme qu'attend la feuille. */
export interface SheetHint {
  /** la ligne visée (clé du niveau de coup de pouce) */
  lineText: string;
  text: string;
  label: string;
  /** les atomes qu'il reste à écrire si la ligne en cours est un bon début, sinon null */
  rest: string[] | null;
}

export interface SheetEngine {
  readonly kind: 'reasoner' | 'packs';
  judge(chips: Chip[], text: string, opts: { verify: boolean }): Verdict;
  commit(v: Verdict): void;
  /** repartir de l'énoncé (effacer une ligne) */
  reset(): void;
  goalReached(): boolean;
  claimDecisions(): (boolean | null)[] | null;
  statedValues(): Record<string, number[]>;
  /** la ligne d'un choix est la réponse, établie */
  isAnswer(line: string): boolean;
  /** la ligne d'un choix est la réponse et elle est écrite */
  hasAnswerLine(line: string): boolean;
  /** QCM à plusieurs bonnes réponses : le choix est une des solutions de la réponse */
  answerContains(line: string): boolean;
  hint(chips: Chip[], text: string): SheetHint | null;
}

// ── ancien moteur ──────────────────────────────────────────────────────────

export function reasonerEngine(model: ScratchModel): SheetEngine {
  let st: ReasonerState = createState(model);
  return {
    kind: 'reasoner',
    judge: (chips, _text, opts) => judge(st, chips, opts),
    commit: (v) => commit(st, v),
    reset: () => {
      st = createState(model);
    },
    goalReached: () => goalReached(st),
    claimDecisions: () => claimDecisions(st),
    statedValues: () => statedValues(st),
    isAnswer: (line) => isAnswer(st, line),
    hasAnswerLine: (line) => isAnswer(st, line) && hasLine(st, parseText(line)),
    answerContains: (line) => answerContains(st, line),
    hint: (chips) => {
      const r = reasonerHint(st, chips);
      return r ? { lineText: r.lineText, text: r.text, label: r.label, rest: r.rest } : null;
    },
  };
}

// ── moteur v2 ──────────────────────────────────────────────────────────────

/** Comparaison de textes de lignes : sans espaces, « ÷ » écrit « / ». */
export function lineKey(text: string): string {
  return text.replace(/\s+/g, '').replace(/÷/g, '/');
}

const OU = /\s+ou\s+/;

/** Clé d'une écriture : celle de `lineKey`, et « √(5) » = « √5 », « e^(2) » = « e^2 » = « e² ». */
const SUP: Record<string, string> = {
  '⁰': '0',
  '¹': '1',
  '²': '2',
  '³': '3',
  '⁴': '4',
  '⁵': '5',
  '⁶': '6',
  '⁷': '7',
  '⁸': '8',
  '⁹': '9',
  ⁿ: 'n',
  ᵏ: 'k',
  ᵐ: 'm',
  '⁺': '+',
  '⁻': '−',
  '⁽': '(',
  '⁾': ')',
};
/** « 3ⁿ⁺¹ » s'écrit aussi « 3^(n + 1) » (exposant tapé au clavier) : même écriture. PUR. */
function plainExponents(text: string): string {
  return text.replace(/[⁰¹²³⁴⁵⁶⁷⁸⁹ⁿᵏᵐ⁺⁻⁽⁾]+/gu, (m) =>
    m === '²' || m === '³' ? m : `^(${[...m].map((c) => SUP[c] ?? c).join('')})`,
  );
}

function writingKey(text: string): string {
  return lineKey(plainExponents(text))
    .replace(/√\(([\p{L}\p{N},]+)\)/gu, '√$1')
    .replace(/\^\((−?[\p{L}\p{N},]+)\)/gu, '^$1')
    .replace(/\^2(?![\p{N},])/gu, '²')
    .replace(/\^3(?![\p{N},])/gu, '³');
}

/**
 * « x = 2 » n'est PAS la réponse quand la feuille a établi « x = −1 ou x = 2 » sans l'écrire seule : c'est une des
 * solutions (le QCM « 2 seulement » est faux). Le moteur v2 lisait la disjonction comme une chaîne « x = (−1 ou x)
 * = 2 » et cochait la dernière solution (audit du 29/10/2026 : 9 feuilles cochaient une mauvaise réponse). PUR.
 */
export function oneOfSeveral(lines: readonly { text: string }[], line: string): boolean {
  if (OU.test(line)) return false;
  const k = writingKey(line);
  if (lines.some((l) => writingKey(l.text) === k)) return false;
  // « x = 1 ou x = 1 » (racine double) : une seule solution, ce n'est pas « une parmi plusieurs »
  return lines.some((l) => {
    if (!OU.test(l.text)) return false;
    const parts = l.text.split(OU).map(writingKey);
    return parts.includes(k) && parts.some((p) => p !== k);
  });
}

const NUM_RE = /^−?\d+(?:,\d+)?$/u;
const num = (t: string) => Number(t.replace(/\s/g, '').replace('−', '-').replace(',', '.'));

/**
 * Le point « x = 0 » est-il dans l'ensemble réponse (« x < 3 », « 0 ≤ x ≤ 9 », « p = 0,3 ou p = 0,7 ») ? Pour un QCM
 * à plusieurs bonnes réponses (« coche les solutions »). null si l'un des deux ne se lit pas (bornes non
 * numériques : exp(2)…). PUR.
 */
export function pointInAnswer(answer: string, line: string): boolean | null {
  const pt = /^\s*([\p{L}][\p{L}\p{N}₀-₉′]*)\s*=\s*(.+?)\s*$/u.exec(line);
  if (!pt) return null;
  const [, name, raw] = pt;
  const v = valueOf(raw!);
  if (Number.isNaN(v)) return null;
  let unread = false;
  const holds = answer.split(OU).some((part) => {
    const toks = part.split(/\s*(<|>|≤|≥|=)\s*/u).map((t) => t.trim());
    if (toks.length < 3 || toks.length % 2 === 0) return ((unread = true), false);
    const val = (t: string): number => (t === name ? v : valueOf(t));
    for (let i = 1; i < toks.length; i += 2) {
      const a = val(toks[i - 1]!),
        b = val(toks[i + 1]!);
      if (Number.isNaN(a) || Number.isNaN(b)) return ((unread = true), false);
      const op = toks[i];
      const ok =
        op === '<'
          ? a < b
          : op === '>'
            ? a > b
            : op === '≤'
              ? a <= b
              : op === '≥'
                ? a >= b
                : a === b;
      if (!ok) return false;
    }
    return true;
  });
  return holds ? true : unread ? null : false;
}

/** Un nombre (« −0,5 ») ou une fraction (« 2 ÷ 3 », « 2/3 ») ; NaN sinon. */
function valueOf(t: string): number {
  const c = t.replace(/\s/g, '');
  if (NUM_RE.test(c)) return num(c);
  const f = /^(−?\d+(?:,\d+)?)[÷/](\d+(?:,\d+)?)$/u.exec(c);
  return f ? num(f[1]!) / num(f[2]!) : Number.NaN;
}

/** « Titre : suite. » → titre et texte d'une bulle. */
export function splitMessage(message: string | undefined): { title: string; text?: string } {
  const m = (message ?? '').trim();
  if (!m) return { title: '' };
  const cut = m.search(/[:?!.](\s|$)/);
  if (cut < 0) return { title: m };
  const sep = m[cut]!;
  const title = sep === ':' ? m.slice(0, cut).trim() : m.slice(0, cut + 1).trim();
  const text = m.slice(cut + 1).trim();
  return text ? { title, text: text[0]!.toUpperCase() + text.slice(1) } : { title };
}

/**
 * La ligne est le début d'une ligne du corrigé (« u(x) = x² » de « u(x) = x² − 1 », « a = b »
 * d'une chaîne « a = b = c ») : elle peut encore continuer, on ne la valide pas d'office.
 */
export function continuesInPath(text: string, path: readonly string[]): boolean {
  const k = lineKey(text);
  if (!k) return false;
  return path.some((p) => {
    const pk = lineKey(p);
    return pk.length > k.length && pk.startsWith(k);
  });
}

/**
 * Traduit un verdict du moteur v2 dans les genres de la feuille. Sans demande de
 * vérification, une ligne fausse ou injustifiée attend (« Ta ligne est finie ? »), comme
 * avec l'ancien moteur ; un piège se montre tout de suite.
 */
export function packsViewVerdict(
  v: PacksVerdict,
  opts: { verify: boolean; continues: boolean; decided?: { index: number; truth: boolean }[] },
): Verdict {
  const { title, text } = splitMessage(v.message);
  const atoms: string[] = [];
  switch (v.status) {
    case 'step':
    case 'goal': {
      const gestures = v.gestures ?? [];
      const label = gestures.map((g) => g.label).join(', puis ') || 'Calculer';
      return {
        kind: opts.continues && !opts.verify ? 'ready' : 'step',
        atoms,
        hora: v.hora ?? 0,
        rule: {
          id: gestures[0]?.ruleId ?? 'packs',
          card: '',
          label,
          hora: v.hora ?? 0,
          why:
            gestures
              .map((g) => g.why)
              .filter(Boolean)
              .join(' ') || label,
        },
        ...(v.status === 'goal' ? { final: true } : {}),
        ...(opts.decided?.length ? { decided: opts.decided } : {}),
      } as Verdict;
    }
    case 'trap':
      return {
        kind: 'trap',
        atoms,
        title: v.trap?.title ?? title,
        message: v.trap?.why ?? text ?? '',
        trap: { title: v.trap?.title ?? title, why: v.trap?.why ?? '' } as Trap,
      };
    case 'useless':
      if (/^Rien de nouveau/.test(v.message ?? ''))
        return { kind: 'dup', atoms, title, message: text };
      return { kind: 'useless', atoms, title, message: text };
    case 'unjustified':
      if (!opts.verify) return { kind: 'pending', atoms };
      // juste mais d'un saut (D7) : se garde, sans HORA
      if (v.kind === 'kept') return { kind: 'useless', atoms, title, message: text, jump: true };
      return { kind: 'unjustified', atoms, title, message: text };
    case 'wrong':
      if (!opts.verify) return { kind: 'pending', atoms };
      return { kind: 'false', atoms, title: title || 'Ce n’est pas juste', message: text };
    default: {
      // ligne illisible (souvent : pas finie)
      const kind: VerdictKind = opts.verify ? 'syntax' : opts.continues ? 'building' : 'open';
      return {
        kind,
        atoms,
        title: 'Je n’arrive pas à lire ta ligne',
        message: v.error?.msg ?? v.message ?? '',
      };
    }
  }
}

export function packsEngine(m: PacksModule, model: ScratchModel & { level?: string }): SheetEngine {
  let sheet: PacksSheet = m.openPacksSheet(model);
  const path = [
    ...(model.kernel?.path ?? []),
    ...(model.kernel?.answer ? [model.kernel.answer] : []),
  ];
  // verdicts traduits → verdicts du moteur v2, pour les enregistrer
  const raw = new WeakMap<Verdict, PacksVerdict>();
  // référence : l'ancien moteur après tout le corrigé (calculée une fois, à la demande) ; null s'il ne finit pas
  let ref: ReasonerState | null | undefined;
  const reference = (): ReasonerState | null => {
    if (ref !== undefined) return ref;
    ref = null;
    try {
      const st = createState(model);
      for (const l of model.kernel?.path ?? []) {
        const v = judge(st, sheetLineChips(model, l), { verify: true });
        if (v.kind === 'step' || v.kind === 'ready') commit(st, v);
      }
      // une réécriture sans forme imposée (« A = 6 ÷ 8 » pour « A = 3 ÷ 4 ») : l'ancien moteur ne distingue pas les
      // écritures égales, il ne sert pas de référence
      const rewriting = model.kernel?.goal?.kind === 'simplify' && !model.kernel?.form;
      if (goalReached(st) && !claimDecisions(st) && !rewriting) ref = st;
    } catch {
      ref = null;
    }
    return ref;
  };
  // la ligne d'un choix est la réponse : établie par le moteur, ou écrite telle quelle
  // sur une feuille finie (limites, intégrales… se jugent dans des sous-feuilles)
  const isWrittenAnswer = (line: string): boolean => {
    if (oneOfSeveral(sheet.lines, line)) return false;
    // l'énoncé recopié n'est jamais la réponse (« a² = 15a + 375 » quand on demande de tout passer à gauche)
    const start = model.kernel?.start;
    if (start && start !== model.kernel?.answer && writingKey(start) === writingKey(line))
      return false;
    // feuille finie : l'ancien moteur, corrigé joué en entier, dit seul quelles écritures sont la réponse
    // (« 6x(x² − 1)² » pour « 3 × 2x × (x² − 1)² », « 0 < x < 1 » mais pas « x < 1 » sous condition)
    const ref = m.packsDone(sheet) ? reference() : null;
    if (ref) return isAnswer(ref, line);
    if (m.packsHasAnswer(sheet, line)) return true;
    // une trace Python passe par des états qui ne sont pas la réponse (« n = 2 » avant « n = 8 ») :
    // seul le moteur dit ce qui est la réponse
    // l'ancien juge de ces sortes dit seul ce qui est la réponse (une étape du corrigé, « y = C ÷ (1 + x²) », n'est
    // pas la solution de l'équation complète)
    if (['python', 'ode', 'integral', 'partial', 'riemann'].includes(model.kernel?.plugin ?? ''))
      return false;
    if (!m.packsDone(sheet)) return false;
    const k = writingKey(line);
    // feuille finie : la réponse du modèle est trouvée, même si le corrigé conclut sur une autre écriture
    // (« (2 + 1 ÷ x) ÷ (1 − 3 ÷ x) → 2 » établit « (2x + 1) ÷ (x − 3) → 2 »)
    const answer = model.kernel?.answer;
    if (answer && writingKey(answer) === k) return true;
    // toute la chaîne « uₙ₊₁ = 3uₙ = 3 × 3ⁿ = 3ⁿ⁺¹ » : ses deux bouts forment la réponse
    const members = OU.test(line) ? [] : line.split(/=(?![^(]*\))/);
    if (
      answer &&
      members.length > 2 &&
      writingKey(`${members[0]}=${members.at(-1)}`) === writingKey(answer)
    )
      return true;
    // une ligne écrite : une étape du corrigé peut être une bonne réponse (« AC² = AB² + BC² »)
    return sheet.lines.some((l) => writingKey(l.text) === k);
  };
  return {
    kind: 'packs',
    judge(_chips, text, opts) {
      if (!text.trim()) return { kind: 'empty', atoms: [] };
      const v = m.judgePacksLine(sheet, text);
      let decided: { index: number; truth: boolean }[] | undefined;
      if (v.status === 'step' || v.status === 'goal') {
        const before = m.packsClaimDecisions(sheet);
        if (before) {
          const after = m.packsClaimDecisions(m.commitPacksLine(sheet, v)) ?? [];
          decided = after.flatMap((truth, index) =>
            truth !== null && before[index] === null ? [{ index, truth }] : [],
          );
        }
      }
      const view = packsViewVerdict(v, {
        verify: opts.verify,
        continues: continuesInPath(text, path),
        decided,
      });
      raw.set(view, v);
      return view;
    },
    commit(view) {
      const v = raw.get(view);
      if (v) sheet = m.commitPacksLine(sheet, v);
    },
    reset() {
      sheet = m.openPacksSheet(model);
    },
    goalReached: () => m.packsDone(sheet),
    claimDecisions: () => m.packsClaimDecisions(sheet),
    statedValues: () => m.packsStatedValues(sheet),
    isAnswer: (line) => isWrittenAnswer(line),
    hasAnswerLine: (line) => isWrittenAnswer(line),
    // plusieurs bonnes réponses : un point de l'ensemble réponse (« x = 0 » dans « x < 3 »)
    answerContains: (line) =>
      isWrittenAnswer(line) ||
      (m.packsDone(sheet) && !!reference() && answerContains(reference()!, line)) ||
      (m.packsDone(sheet) && pointInAnswer(String(model.kernel?.answer ?? ''), line) === true),
    hint(chips) {
      const h = m.packsHint(sheet);
      if (!h) return null;
      const want = sheetLineChips(model, h.line).flatMap((c) => c.atoms);
      const have = chips.flatMap((c) => c.atoms);
      const prefix = have.length <= want.length && have.every((a, i) => a === want[i]);
      return {
        lineText: h.line,
        text: h.why,
        label: h.gestures?.[0]?.label ?? '',
        rest: prefix ? want.slice(have.length) : null,
      };
    },
  };
}
